import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "reactstrap";
import { usePermissionContext } from "libs/context/PermissionContext";
import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_PAGE_SIZE_OPTIONS,
} from "helpers/GeneralHelper";

export const ALL_ROWS_PAGE_SIZE = -1;
export { DEFAULT_PAGE_SIZE, DEFAULT_PAGE_SIZE_OPTIONS };

export const formatPageSizeLabel = (size) =>
  Number(size) === ALL_ROWS_PAGE_SIZE ? "All" : String(size ?? "");

export const buildPageSizeOptions = (baseOptions, canShowAll) => {
  const options =
    Array.isArray(baseOptions) && baseOptions.length
      ? [...baseOptions]
      : [...DEFAULT_PAGE_SIZE_OPTIONS];
  if (canShowAll && !options.some((size) => Number(size) === ALL_ROWS_PAGE_SIZE)) {
    options.push(ALL_ROWS_PAGE_SIZE);
  }
  return options;
};

export const resolvePageSizeDisplayRange = (pageNumber, pageSize, totalRecords) => {
  const total = Number(totalRecords) || 0;
  if (!total) return { displayFrom: 0, displayTo: 0 };
  if (Number(pageSize) === ALL_ROWS_PAGE_SIZE) {
    return { displayFrom: 1, displayTo: total };
  }
  const page = Number(pageNumber) || 1;
  const size = Number(pageSize) || DEFAULT_PAGE_SIZE;
  const startIndex = (page - 1) * size;
  return {
    displayFrom: startIndex + 1,
    displayTo: Math.min(startIndex + size, total),
  };
};

export const PAGINATION_ELLIPSIS = "ellipsis";

/** Smooth-scroll the page (and any scrollable layout parents) back to the top after pagination. */
export const scrollTablePaginationToTop = () => {
  if (typeof window === "undefined") return;

  const prefersReducedMotion = window.matchMedia?.(
    "(prefers-reduced-motion: reduce)"
  )?.matches;
  const behavior = prefersReducedMotion ? "auto" : "smooth";

  window.scrollTo({ top: 0, left: 0, behavior });
  document.documentElement?.scrollTo?.({ top: 0, left: 0, behavior });
  document.body?.scrollTo?.({ top: 0, left: 0, behavior });

  const pageContent = document.querySelector(".page-content");
  let node = pageContent?.parentElement || null;
  while (node && node !== document.body) {
    const { overflowY } = window.getComputedStyle(node);
    if (
      (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") &&
      node.scrollTop > 0
    ) {
      node.scrollTo({ top: 0, left: 0, behavior });
    }
    node = node.parentElement;
  }
};

/**
 * Truncated page list for table pagination, e.g. [1, 2, 3, 4, 5, "ellipsis", 200].
 */
export const buildPaginationPageItems = (
  currentPage,
  totalPages,
  siblingCount = 1
) => {
  const total = Math.max(0, Number(totalPages) || 0);
  if (!total) return [];

  const current = Math.min(Math.max(1, Number(currentPage) || 1), total);
  const siblings = Math.max(0, Number(siblingCount) || 0);
  const range = (start, end) =>
    Array.from({ length: end - start + 1 }, (_, index) => start + index);

  // first + last + current ± siblings + room for two ellipsis markers
  const maxVisibleWithoutEllipsis = siblings * 2 + 5;
  if (total <= maxVisibleWithoutEllipsis) {
    return range(1, total);
  }

  const leftSibling = Math.max(current - siblings, 1);
  const rightSibling = Math.min(current + siblings, total);
  const showLeftEllipsis = leftSibling > 2;
  const showRightEllipsis = rightSibling < total - 1;

  if (!showLeftEllipsis && showRightEllipsis) {
    const leftItemCount = 3 + 2 * siblings;
    return [...range(1, leftItemCount), PAGINATION_ELLIPSIS, total];
  }

  if (showLeftEllipsis && !showRightEllipsis) {
    const rightItemCount = 3 + 2 * siblings;
    return [1, PAGINATION_ELLIPSIS, ...range(total - rightItemCount + 1, total)];
  }

  return [
    1,
    PAGINATION_ELLIPSIS,
    ...range(leftSibling, rightSibling),
    PAGINATION_ELLIPSIS,
    total,
  ];
};

/**
 * Prefer API pageSize when present.
 * If client requested All (`-1`) and API returns a positive size (e.g. 200), use the API value.
 */
export const resolveServerPageSizeMeta = (
  envelopePageSize,
  requestedPageSize,
  defaultPageSize = DEFAULT_PAGE_SIZE
) => {
  const fromApi = Number(envelopePageSize);
  if (Number.isFinite(fromApi) && fromApi > 0) {
    return fromApi;
  }
  const requested = Number(requestedPageSize);
  if (requested === ALL_ROWS_PAGE_SIZE) {
    return defaultPageSize;
  }
  if (Number.isFinite(requested) && requested > 0) {
    return requested;
  }
  return defaultPageSize;
};

const ServerListPagination = ({
  currentPage = 1,
  rowsPerPage = DEFAULT_PAGE_SIZE,
  totalPages = 0,
  totalRecords = 0,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  className = "",
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggleDropdown = () => setDropdownOpen((open) => !open);
  const { permissionData } = usePermissionContext() || {};
  const canShowAllRows = Boolean(permissionData?.CandidateShowAllAccess);

  const resolvedCurrentPage = Number(currentPage) || 1;
  const resolvedRowsPerPage =
    Number(rowsPerPage) === ALL_ROWS_PAGE_SIZE
      ? ALL_ROWS_PAGE_SIZE
      : Number(rowsPerPage) || DEFAULT_PAGE_SIZE;
  const resolvedTotalPages = Number(totalPages) || 0;
  const resolvedTotalRecords = Number(totalRecords) || 0;
  const { displayFrom, displayTo } = resolvePageSizeDisplayRange(
    resolvedCurrentPage,
    resolvedRowsPerPage,
    resolvedTotalRecords
  );
  const resolvedPageSizeOptions = useMemo(
    () => buildPageSizeOptions(pageSizeOptions, canShowAllRows),
    [pageSizeOptions, canShowAllRows]
  );

  const pageItems = useMemo(
    () => buildPaginationPageItems(resolvedCurrentPage, resolvedTotalPages),
    [resolvedCurrentPage, resolvedTotalPages]
  );

  const handlePrevPage = () => {
    if (resolvedCurrentPage <= 1) return;
    scrollTablePaginationToTop();
    onPageChange?.(resolvedCurrentPage - 1);
  };

  const handleNextPage = () => {
    if (!resolvedTotalPages || resolvedCurrentPage >= resolvedTotalPages) return;
    scrollTablePaginationToTop();
    onPageChange?.(resolvedCurrentPage + 1);
  };

  const handlePageNumberClick = (page) => {
    if (page === resolvedCurrentPage) return;
    scrollTablePaginationToTop();
    onPageChange?.(page);
  };

  if (!resolvedTotalRecords) return null;

  return (
    <div
      className={`d-flex justify-content-end align-items-center px-3 py-2 border-top bg-white ${className}`.trim()}
    >
      <div className="d-flex align-items-center gap-2 me-3">
        <span className="text-muted small">Rows per page:</span>
        <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown}>
          <DropdownToggle caret color="light" size="sm" className="border bg-white">
            {formatPageSizeLabel(resolvedRowsPerPage)}
          </DropdownToggle>
          <DropdownMenu>
            {resolvedPageSizeOptions.map((size) => (
              <DropdownItem key={size} onClick={() => onPageSizeChange?.(size)}>
                {formatPageSizeLabel(size)}
              </DropdownItem>
            ))}
          </DropdownMenu>
        </Dropdown>
      </div>
      <span className="text-muted small me-3">
        {displayFrom}–{displayTo} of {resolvedTotalRecords}
      </span>
      <div className="table-pagination-nav">
        <Button
          color="link"
          className="table-pagination-arrow-btn text-muted"
          disabled={resolvedCurrentPage <= 1}
          onClick={handlePrevPage}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M15.41 16.58L10.83 12l4.58-4.59L14 6l-6 6 6 6z" />
          </svg>
        </Button>
        {pageItems.map((item, index) =>
          item === PAGINATION_ELLIPSIS ? (
            <span
              key={`ellipsis-${index}`}
              className="table-pagination-ellipsis"
              aria-hidden="true"
            >
              …
            </span>
          ) : (
            <Button
              key={item}
              color="link"
              className={`table-pagination-page-btn ${
                item === resolvedCurrentPage ? "is-active text-primary" : "text-muted"
              }`}
              onClick={() => handlePageNumberClick(item)}
              aria-current={item === resolvedCurrentPage ? "page" : undefined}
            >
              {item}
            </Button>
          )
        )}
        <Button
          color="link"
          className="table-pagination-arrow-btn text-muted"
          disabled={!resolvedTotalPages || resolvedCurrentPage >= resolvedTotalPages}
          onClick={handleNextPage}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M8.59 16.58L13.17 12 8.59 7.41 10 6l6 6-6 6z" />
          </svg>
        </Button>
      </div>
    </div>
  );
};

ServerListPagination.propTypes = {
  currentPage: PropTypes.number,
  rowsPerPage: PropTypes.number,
  totalPages: PropTypes.number,
  totalRecords: PropTypes.number,
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
  className: PropTypes.string,
};

export default ServerListPagination;
